import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { InterviewerCard } from '../../components/interviewers/InterviewerCard';
import { InterviewerFilter } from '../../components/interviewers/InterviewerFilter';
import { BookingModalNew } from '../../components/interviewers/BookingModalNew';
import type { InterviewerFilter as InterviewerFilterType, InterviewerCard as InterviewerCardType } from '../../types/interviewer';
import { apiService } from '../../services/api';
import { AdBanner } from '../../components/ads/AdBanner';
import { ADS_ENABLED } from '../../config/ads';

export function InterviewersList() {
  const { t } = useTranslation();
  const [interviewers, setInterviewers] = useState<InterviewerCardType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [availableLanguages, setAvailableLanguages] = useState<string[]>([]);
  const [availableSpecialties, setAvailableSpecialties] = useState<string[]>([]);
  const [selectedInterviewer, setSelectedInterviewer] = useState<InterviewerCardType | null>(null);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);

  const loadInterviewers = async (filter?: InterviewerFilterType) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.interviewers.filter(filter || {});
      setInterviewers(response.interviewers || []);
    } catch (err: any) {
      console.error('Filter request failed:', err);
      setError(err.response?.data?.message || err.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInterviewers();
    
    // Load filter options
    const loadFilterOptions = async () => {
      try {
        const [languages, specialties] = await Promise.all([
          apiService.interviewers.getAvailableLanguages(),
          apiService.interviewers.getAvailableSpecialties(),
        ]);
        setAvailableLanguages(languages);
        setAvailableSpecialties(specialties);
      } catch (error) {
        console.error('Failed to load filter options:', error);
      }
    };
    
    loadFilterOptions();
  }, []);

  const handleFilterChange = (filter: InterviewerFilterType) => {
    loadInterviewers(filter);
  };

  const handleInterviewerSelect = (interviewerId: string) => {
    const interviewer = interviewers.find(e => e.id === interviewerId);
    if (interviewer) {
      setSelectedInterviewer(interviewer);
      setIsBookingModalOpen(true);
    }
  };

  const handleBookingComplete = () => {
    // Refresh the interviewers list to update availability
    loadInterviewers();
  };

  // Convert InterviewerCard to InterviewerDetails format for the BookingModal
  const convertToInterviewerDetails = (interviewer: InterviewerCardType) => {
    return {
      id: Number(interviewer.id),
      userId: 0,
      name: interviewer.profileName,
      email: interviewer.email,
      specialty: interviewer.specialty,
      company: interviewer.company || '',
      experience: interviewer.experience || '',
      languages: interviewer.languages,
      profilePictureUrl: interviewer.profilePictureUrl || interviewer.profilePicture || null,
      rating: interviewer.rating,
      linkedinUrl: interviewer.linkedinUrl,
      city: interviewer.city,
      country: interviewer.country,
      totalInterviews: interviewer.totalInterviews
    };
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-2 border-stitch-primary border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-4 rounded-stitch-lg border border-stitch-danger/40 bg-stitch-danger-soft p-4 shadow-stitch-card">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-stitch-danger" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-stitch-fg-soft">{error}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => loadInterviewers()}
          className="mt-3 rounded-stitch-md bg-stitch-surface px-3 py-1.5 text-sm font-medium text-stitch-danger shadow-stitch-card transition hover:bg-stitch-danger-soft"
        >
          {t('common.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stitch-canvas">
      <div className="border-b border-stitch-border bg-gradient-to-br from-stitch-primary-soft via-stitch-surface to-stitch-accent-soft">
        <div className="container mx-auto max-w-7xl px-4 py-10 sm:px-6 md:py-12 lg:px-8">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div className="max-w-2xl">
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-stitch-primary">
                <span className="material-symbols-outlined text-base">groups</span>
                {t('interviewers.title', 'Experts')}
              </p>
              <h1 className="mt-3 text-3xl font-bold tracking-tight text-stitch-fg md:text-4xl">
                {t('interviewers.title', 'Find Interviewers')}
              </h1>
              <p className="mt-3 text-base text-stitch-fg-soft">
                {t('interviewers.subtitle', 'Browse and book interviews with experienced professionals')}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-2 rounded-stitch-lg border border-stitch-border bg-stitch-surface px-4 py-2 text-sm font-medium text-stitch-fg shadow-stitch-card">
                <span className="material-symbols-outlined text-stitch-accent">auto_awesome</span>
                Curated specialists
              </span>
              <span className="inline-flex items-center gap-2 rounded-stitch-lg border border-stitch-border bg-stitch-surface px-4 py-2 text-sm font-medium text-stitch-fg shadow-stitch-card">
                <span className="material-symbols-outlined text-stitch-success">verified</span>
                Verified profiles
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">

      {/* Mobile toggle for filters */}
      <div className="block lg:hidden mb-4">
        <button
          type="button"
          className="flex w-full items-center justify-between rounded-stitch-lg bg-stitch-primary px-4 py-2.5 font-medium text-white shadow-stitch-card transition hover:bg-stitch-primary-hover"
          onClick={() => setFilterOpen(!filterOpen)}
        >
          <span className="inline-flex items-center gap-2">
            <span className="material-symbols-outlined text-xl">tune</span>
            Filters
          </span>
          <span className="material-symbols-outlined text-xl">{filterOpen ? 'expand_less' : 'expand_more'}</span>
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        {/* Filter sidebar */}
        <div className={`${filterOpen ? 'block' : 'hidden'} lg:col-span-3 lg:block`}>
          <div className="sticky top-4 rounded-stitch-xl border border-stitch-border bg-stitch-surface p-4 shadow-stitch-card">
            <InterviewerFilter
              onFilterChange={handleFilterChange}
              availableLanguages={availableLanguages}
              availableSpecialties={availableSpecialties}
            />
          </div>
        </div>

        {/* Interviewers grid */}
        <div className="lg:col-span-6">
          {interviewers.length === 0 ? (
            <div className="rounded-stitch-2xl border border-dashed border-stitch-border bg-stitch-surface p-10 text-center shadow-stitch-card">
              <span className="material-symbols-outlined mx-auto block text-5xl text-stitch-muted">search_off</span>
              <p className="mb-4 mt-4 text-lg font-medium text-stitch-fg-soft">
                No interviewers found matching your criteria.
              </p>
              <button
                type="button"
                className="rounded-stitch-lg bg-stitch-primary px-6 py-2 font-medium text-white shadow-stitch-card transition hover:bg-stitch-primary-hover"
                onClick={() => {
                  handleFilterChange({});
                  setFilterOpen(false);
                }}
              >
                Reset Filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              {interviewers.map((interviewer) => (
                <InterviewerCard
                  key={interviewer.id}
                  interviewer={interviewer}
                  onSelect={handleInterviewerSelect}
                />
              ))}
            </div>
          )}
        </div>

        {ADS_ENABLED && (
          <aside className="hidden lg:col-span-3 lg:block">
            <div className="sticky top-4">
              <AdBanner slot="sidebar-rectangle" size="rectangle" />
            </div>
          </aside>
        )}
      </div>

      {/* Booking Modal */}
      {selectedInterviewer && (
        <BookingModalNew
          isOpen={isBookingModalOpen}
          onClose={() => {
            setIsBookingModalOpen(false);
            setSelectedInterviewer(null);
          }}
          interviewer={convertToInterviewerDetails(selectedInterviewer)}
          onBookingComplete={handleBookingComplete}
        />
      )}
      </div>
    </div>
  );
} 